import React, { useState, useEffect, useRef } from 'react';
import style from './add-message.module.scss';
import { Select, Empty } from 'antd';
import BackIcon from '../../../../../../../assets/svg/solo-chat/back.svg';
import DownIcon from '../../../../../../../assets/svg/solo-chat/down-2.svg';
import { ReactComponent as SearchIcon } from "../../../../../../../assets/svg/solo-chat/searchIcon.svg";
import { contentTypes } from '../../../../../Solo/chat/pages/content/constants';
import { AddContent } from './AddContent';
import { useQuery, useAppAndBranchId, usePermissions } from '../../../../chat/hooks';
import { api } from '../../../../chat/pages/content/api';
import { AddContentType } from '../../../../chat/pages/qna/createDialog/components/AddMessage';
import { AttachedMsg } from '../types';
import CodeEditor from '../../../codeEditor/CodeEditor';
import { uuid } from '../../../../../../../helpers/commonFunction';
import { Editor } from '../../../../chat/pages/qna/components';

type Props = {
	isOpen: boolean;
	isEdit?: boolean;
	close: () => void;
	onAdd?: (data: AttachedMsg) => void;
	onEdit?: (index: number, data: AttachedMsg) => void;
	initialValue?: {
		contentType: string;
		contentId: string;
		index: number;
		title: string;
		source: string;
		isDynamic: boolean;
	};
	attachedMsgs: AttachedMsg[];
};

export function AddMessagePanel(props: Props) {
	const { initialValue, isOpen, attachedMsgs, isEdit } = props;
	const [isAddOpen, setIsAddOpen] = useState(false);

	const [msgType, setMsgType] = useState<'static' | 'dynamic'>('static');

	// for static msg type
	const [contentType, setContentType] = useState('');
	const [contentId, setContentId] = useState('');

	// for dynamic msg type
	const [source, setSource] = useState('');
	const [title, setTitle] = useState('');
	const [isEditorOpen, setIsEditorOpen] = useState(false);

	const [appId, branchId] = useAppAndBranchId();
	let { data: contents, refetch } = useQuery(
		() => api.getContents(appId, branchId, contentType),
		contentType,
		contentType !== '',
	);
	const selectedContentIds = attachedMsgs
		.filter((el) => !el.isDynamic)
		.map((el: any) => el.contentId);
	contents = !contents
		? []
		: isEdit
		? contents
		: contents.filter((item) => !selectedContentIds.includes(item.contentId));

	const [error, setError] = useState({
		contentType: '',
		contentId: '',
		title: '',
		source: '',
	});

	function validate() {
		const error: Record<string, string> = {};
		if (msgType === 'static') {
			if (!contentType.length) {
				error['contentType'] = 'Select the content type';
			}
			if (!error.contentType && !contentId.length) {
				error['contentId'] = 'Select the content';
			}
		}

		if (msgType === 'dynamic') {
			if (!title.length) {
				error['title'] = "Title can't be empty";
			}
			if (!source.length) {
				error['source'] = "Source can't be empty";
			}
		}

		const errorLen = Object.keys(error).length;
		if (errorLen) {
			setError(error as any);
		}
		return errorLen === 0;
	}

	useEffect(() => {
		if (!isOpen) {
			setContentType('');
			setContentId('');
			setSource('');
			setTitle('');
			setMsgType('static');
			setError({ contentType: '', contentId: '', title: '', source: '' });
		}
	}, [isOpen]);

	useEffect(() => {
		if (initialValue) {
			setContentType(initialValue.contentType ?? '');
			setContentId(initialValue.contentId ?? '');
			setSource(initialValue.source ?? '');
			setTitle(initialValue.title ?? '');
			setMsgType(initialValue.isDynamic ? 'dynamic' : 'static');
		}
	}, [initialValue]);

	return (
		<div
			style={{ transform: isOpen ? 'translateX(0%)' : 'translateX(-100%)' }}
			className={style.addMessagePanel}
		>
			<Header
				{...props}
				validate={validate}
				contentId={contentId}
				msgType={msgType}
				source={source}
				title={title}
			/>

			<div className={style.messageSelect}>
				<label>Message Type</label>
				<SelectContent
					placeholder="Select Message Type"
					value={msgType}
					onChange={(value: any) => {
						setMsgType(value);
					}}
				>
					<Select.Option key="static" value="static" label="Static">
						<div>
							<p>Static</p>
						</div>
					</Select.Option>
					<Select.Option key="dynamic" value="dynamic" label="Dynamic">
						<div>
							<p>Dynamic</p>
						</div>
					</Select.Option>
				</SelectContent>
			</div>

			{msgType === 'dynamic' && (
				<>
					<div className={style.inputWrapper}>
						<label htmlFor="title">Title</label>
						<input
							id="title"
							value={title}
							onChange={(e) => {
								setError((prev) => ({ ...prev, title: '' }));
								setTitle(e.target.value);
							}}
							placeholder="Enter title"
						/>
						{error.title && (
							<span style={{ top: 0 }} className={style.error}>
								{error.title}
							</span>
						)}
					</div>
					<div
						role="button"
						className={style.codePreviewWrapper}
						onClick={() => {
							if (!isEditorOpen) {
								setError((prev) => ({ ...prev, source: '' }));
								setIsEditorOpen(true);
							}
						}}
					>
						{source.trim().length > 0 && !isEditorOpen ? (
							<Editor
								theme="vs"
								style={{
									border: '1px solid rgba(144, 155, 173, 1)',
								}}
								background="#FFFFFF"
								foreground="#292D32"
								readOnly
								value={source}
								height="90px"
							/>
						) : (
							<button className={style.codePreview} data-empty>
							 {isEditorOpen ? 'Editing source' : 'Click to edit your source'}
							</button>
						)}
						{error.source && (
							<span style={{ top: 0 }} className={style.error}>
								{error.source}
							</span>
						)}
						{isEditorOpen && (
							<CodeEditor
								type="solo"
								isMessageNode
								id="1"
								values={source}
								handleExpression={(_: any, values: any) => {
									let source = values.trim()
									if(source === '""') {
										source = ''
									  setError(prev => ({...prev, source: "Source can't be empty"}))
									}
									setSource(source);
								}}
								close={() => {
									setIsEditorOpen(false);
								}}
							/>
						)}
					</div>
				</>
			)}

			{msgType === 'static' && (
				<>
					<div className={style.messageSelect}>
						<label>Select Content</label>
						<SelectContent
							placeholder="Select Content Type"
							value={contentType}
							onChange={(value) => {
								setError((err) => ({ ...err, contentType: '' }));
								setContentType(value);
								setContentId('');
							}}
						>
							{contentTypes.map((el) => {
								return (
									<Select.Option
										key={el.value}
										value={el.value}
										label={el.label}
									>
										<div>
											<p>{el.label}</p>
										</div>
									</Select.Option>
								);
							})}
						</SelectContent>
						{error.contentType && (
							<span className={style.error}>{error.contentType}</span>
						)}
					</div>

					{contentType && (
						<div className={style.messageSelect}>
							<SelectContent
								placeholder="Select Content"
								value={contentId}
								setIsAddOpen={setIsAddOpen}
								contentType={contentType}
								searchIpPlaceholder={`Search...`}
								onChange={(value) => {
									setContentId(value);
									setError((err) => ({ ...err, contentId: '' }));
								}}
							>
								{contents.map((el, i) => {
									return (
										<Select.Option
											key={el.contentId}
											value={el.contentId}
											label={el.contentId}
										>
												<div className="textOption">
													<p className="text-ellipsis">
														<span>
															{
																(el.contentValue as any)[
																	labelMap[el.contentType]
																]
															}
														</span>
													</p>
													<hr />
												</div>
										</Select.Option>
									);
								})}
							</SelectContent>
							{error.contentId && (
								<span className={style.error}>{error.contentId}</span>
							)}
						</div>
					)}
					<AddContent
						contentType={contentType}
						refetchContent={refetch}
						close={() => setIsAddOpen(false)}
						isOpen={isAddOpen}
						onCreate={(contentId) => setContentId(contentId)}
					/>
				</>
			)}
		</div>
	);
}

type SelectProps = {
	value: string;
	onChange: (value: string) => void;
	children: React.ReactNode;
	placeholder: string;
	classes?: string;
	notFoundContent?: React.ReactNode;
	setIsAddOpen?: Function;
	contentType?: string;
	searchIpPlaceholder?: string;
};

export function SelectContent(props: SelectProps) {
	const { value, onChange, placeholder, classes = '', notFoundContent, setIsAddOpen, contentType, searchIpPlaceholder } = props;
	const [inputText, setInputText] = useState("");
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [data, setData] = useState<React.ReactNode[]>();
	const inputRef = useRef(null);
	let values = Array.isArray(props.children) ? props.children : [props.children];
	const canCreate = usePermissions('content.Create')

	useEffect(() => {
		setData(values);
	}, [props.children]);

	let searchHandler = (value: string) => {
		const searchValue = value?.toLowerCase().trim();

		const valData = values.filter(val => {
			const level1 = val?.props?.children;
			const level2 = level1?.props?.children?.[0];
			const level3 = level2?.props?.children;
			const level4 = level3?.props?.children;

			const text = level4?.toLowerCase().trim();

			return text?.includes(searchValue);
		});
		setData(valData);
		setInputText(value);
	};

	const defaultNotFoundContent = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

	return (
		<Select
			dropdownClassName="solo-message-node-select"
			className={`${style.select} solo-message-node-select ${classes}`}
			onChange={onChange}
			suffixIcon={<img src={DownIcon} alt="down" />}
			value={value.length ? value : undefined}
			placeholder={placeholder}
			size="small"
			open={dropdownOpen}
			onDropdownVisibleChange={(open) => {
				// to prevent closing of dropdown onclicking inside the dropdown
				if (document.activeElement !== inputRef.current && document.activeElement?.tagName?.toLowerCase() !== "button") {
					setDropdownOpen(open);
				}
			}}
			notFoundContent={
				notFoundContent ? notFoundContent : defaultNotFoundContent
			}
			dropdownRender={(menu) => (
				<div>
					{setIsAddOpen &&
						<>
							{canCreate &&
								<AddContentType
									contentType={contentType || ""}
									onClick={() => {
										setIsAddOpen && setIsAddOpen(true);
										setDropdownOpen(false);
									}}
								/>
							}
							{contentType === "text" && <div className={style.searchDiv}>
								<div className={style.addMessagePanelSearchIcon}>
									<SearchIcon width={16} height={16} />
								</div>
								<input
									className={style.addMessagePanelInput}
									onBlur={() => {
										setDropdownOpen(false);
									}}
									ref={inputRef}
									onChange={(e) => searchHandler(e.target.value)}
									value={inputText}
									type="search"
									placeholder={searchIpPlaceholder ? searchIpPlaceholder : 'Search message...'}
									id="default-search"
								/>
							</div>
							}
						</>
					}
					{menu}
				</div>
			)}
		>
			{data}
		</Select>
	);
}

function Header(
	props: Props & {
		source: string;
		msgType: string;
		title: string;
		contentId: string;
		validate: () => boolean;
	},
) {
	const {
		contentId,
		onAdd,
		close,
		isEdit,
		onEdit,
		initialValue,
		validate,
		msgType,
		title,
		source,
	} = props;
	return (
		<div className={style.header}>
			<div>
				<button onClick={props.close}>
					<img src={BackIcon} alt="back" />
				</button>
				<p>{isEdit ? 'Edit' : 'Add'} Message</p>
			</div>
			<div>
				<button onClick={close}>Cancel</button>
				<button
					onClick={() => {
						if (!validate()) {
							return;
						}
						const attachedMsg: AttachedMsg =
							msgType === 'static'
								? {
										isDynamic: false,
										contentId,
										_id: uuid(),
								  }
								: {
										isDynamic: true,
										source,
										title,
										_id: uuid(),
								  };
						if (onAdd) {
							onAdd(attachedMsg);
						} else if (onEdit && initialValue) {
							onEdit(initialValue.index, attachedMsg);
						}
						close();
					}}
				>
					Submit
				</button>
			</div>
		</div>
	);
}

export const labelMap = {
	text: 'text',
	simpleChoice: 'title',
	cardChoice: 'title',
	pdf: 'description',
	image: 'description',
	autoComplete: 'title',
	adaptiveCard: 'title',
	markdown: 'title',
	cardCarousel: 'title',
};
